import { Component, Injector, Input, OnInit } from '@angular/core'
import { FormControl } from '@angular/forms'
import { TranslateService } from '@ngx-translate/core'

import { filter, finalize, switchMap, takeUntil, tap } from 'rxjs/operators'

import { ServiceRecordListDatasource } from './service-record-list.datasource'
import { ServiceRecordService } from './service-record.service'
import { ApiTableComponent } from '@shared/components/tables/api-table.component'
import { ServiceRecord, ServiceRecordStatus } from './service-record.model'
import { DialogService } from '@shared/dialogs/dialog.service'
import { ServiceRecordEditDialogComponent } from '../service-record-edit-dialog/service-record-edit-dialog.component'

@Component({
  selector: 'lin-service-record-list',
  templateUrl: './service-record-list.component.html',
  styleUrls: ['./service-record-list.component.scss'],
  standalone: false,
})
export class ServiceRecordListComponent extends ApiTableComponent implements OnInit {
  @Input() reminderId: number
  @Input() isEditMode: boolean
  @Input() isDeleteMode: boolean

  isEditing = {}
  isDeleting = {}
  dataSource: ServiceRecordListDatasource

  columns = ['date', 'odometer', 'engineHours', 'cost', 'filesAttached', 'note']
  columnsMandatory = ['note']

  constructor(
    private readonly serviceRecordService: ServiceRecordService,
    private readonly translateService: TranslateService,
    private readonly dialog: DialogService,
    protected readonly injector: Injector,
  ) {
    super(injector)
  }

  ngOnInit(): void {
    super.ngOnInit()
    this.dataSource = new ServiceRecordListDatasource(this.reminderId, this.serviceRecordService, this.sort, this.paginator, this.form)
  }

  initForm(): void {
    super.initForm()

    this.form.addControl('status', new FormControl(ServiceRecordStatus.Active))
    if (this.isEditMode) {
      this.columns.push('edit')
      this.columnsMandatory.push('edit')
    }

    if (this.isDeleteMode) {
      this.columns.push('delete')
      this.columnsMandatory.push('delete')
    }
  }

  refresh(): void {
    this.dataSource.refresh().pipe(takeUntil(this.onDestroy$)).subscribe()
  }

  edit(record: ServiceRecord) {
    this.dialog
      .open(ServiceRecordEditDialogComponent, <string>this.translateService.instant('CLIENT.VEHICLES.DOCUMENTS.EDIT_RECORD'), record)
      .afterClosed()
      .pipe(
        filter(Boolean),
        tap(() => (this.isEditing[record.id] = true)),
        switchMap((updatedRecord: ServiceRecord) => this.serviceRecordService.edit(this.reminderId, record.id, updatedRecord)),
        switchMap(() => this.dataSource.refresh()),
        finalize(() => (this.isEditing[record.id] = false)),
        takeUntil(this.onDestroy$),
      )
      .subscribe()
  }

  delete(serviceRecord: ServiceRecord): void {
    const { reminder, id: recordId, note } = serviceRecord
    const dialogTitle = note
      ? (this.translateService.instant('CLIENT.VEHICLES.SERVICE_REMINDERS.DELETE_CONFIRMATION_RECORD', { value: note }) as string)
      : (this.translateService.instant('CLIENT.VEHICLES.SERVICE_REMINDERS.DELETE_CONFIRMATION_RECORD_EMPTY_NOTE') as string)

    this.dialog
      .confirm(this.translateService.instant(dialogTitle) as string)
      .afterClosed()
      .pipe(
        filter(Boolean),
        tap(() => (this.isDeleting[recordId] = true)),
        switchMap(() => this.serviceRecordService.delete(reminder.id, recordId)),
        switchMap(() => this.dataSource.refresh()),
        finalize(() => (this.isDeleting[recordId] = false)),
        takeUntil(this.onDestroy$),
      )
      .subscribe()
  }

  displayDistance(distance: number | null): string {
    if (distance === null) return '-'
    return <string>this.translateService.instant('ABBREVIATIONS.KM', { value: distance })
  }

  displayEngineHours(engineHours: number | null): string {
    if (engineHours === null) return '-'
    return <string>this.translateService.instant('ABBREVIATIONS.H', { value: engineHours })
  }
}
